import { Mongo } from 'meteor/mongo';

/**
 * Create an index, reconciling with whatever is already there.
 *
 * Mongo refuses a second index on a key that already has one under a different
 * name or with different options (IndexOptionsConflict, code 85). Index setup
 * runs at import/startup, so an unhandled rejection there is a boot failure,
 * and a shared `try` around several createIndex calls means the FIRST conflict
 * silently skips every index after it.
 *
 * So: try; on a conflict, drop the offending same-key index and retry once;
 * never throw. A missing index is a performance problem, a throw here is an
 * outage, and a skipped index is a bug that hides until something is slow.
 *
 * TTL conversions are deliberately NOT auto-reconciled -- see below.
 */
export const ensureIndex = async (
  collection: Mongo.Collection<any>,
  keys: Record<string, 1 | -1>,
  options: Record<string, unknown> = {},
): Promise<void> => {
  const raw = collection.rawCollection();
  const name = raw.collectionName;
  try {
    await raw.createIndex(keys as any, options as any);
  }
  catch (error: any) {
    // Both conflict flavours mean "an index with this name/key exists with a
    // different definition". Mongo raises 85 (IndexOptionsConflict) for some
    // option changes but 86 (IndexKeySpecsConflict) for others -- sparse ->
    // partialFilterExpression on an auto-named index verifiably comes back as
    // 86, which is exactly how the tradeSessions reconcile sat broken through
    // a deploy while handling "the" conflict code.
    const conflict = error?.code === 85 || error?.code === 86
      || error?.codeName === 'IndexOptionsConflict' || error?.codeName === 'IndexKeySpecsConflict';
    if (!conflict) {
      console.warn(`ensureIndex(${name}, ${JSON.stringify(keys)}) failed: ${error?.message ?? error}`);
      return;
    }
    try {
      const existing = await raw.indexes();
      const clash = existing.find((i: any) => JSON.stringify(i.key) === JSON.stringify(keys));
      if (!clash) return;
      // Dropping a plain index and rebuilding it as TTL can be a long foreground
      // build plus a delete storm when the TTL first sweeps. That is an operator
      // decision, not a boot side effect -- a migration should do it knowingly.
      if ('expireAfterSeconds' in options && !clash.expireAfterSeconds) {
        console.warn(
          `ensureIndex: ${name} has a non-TTL index ${clash.name} on ${JSON.stringify(keys)}; `
          + 'not converting automatically. Convert it with a migration.',
        );
        return;
      }
      // TTL -> TTL with only the retention changed: adjust in place via collMod.
      // Drop+rebuild here would rebuild the whole index (foreground, and on
      // `logs` that is 200M+ rows) for what Mongo can do as a metadata edit.
      if (typeof options.expireAfterSeconds === 'number' && typeof clash.expireAfterSeconds === 'number'
          && options.expireAfterSeconds !== clash.expireAfterSeconds) {
        console.warn(`ensureIndex: adjusting TTL on ${name}.${clash.name} `
          + `${clash.expireAfterSeconds}s -> ${options.expireAfterSeconds}s via collMod`);
        await collection.rawDatabase().command({
          collMod: name,
          index: { name: clash.name, expireAfterSeconds: options.expireAfterSeconds },
        });
        return;
      }
      console.warn(`ensureIndex: replacing index ${clash.name} on ${name} to match the requested options`);
      await raw.dropIndex(clash.name);
      await raw.createIndex(keys as any, options as any);
    }
    catch (retryError: any) {
      console.warn(`ensureIndex(${name}) could not reconcile: ${retryError?.message ?? retryError}`);
    }
  }
};

/**
 * Create several indexes, each reconciled and each isolated.
 *
 * Isolation is the point: with one `try` around a list of createIndex calls,
 * a conflict on the third silently skips the fourth and fifth. Every index gets
 * its own attempt.
 */
export const ensureIndexes = async (
  collection: Mongo.Collection<any>,
  specs: { keys: Record<string, 1 | -1>; options?: Record<string, unknown> }[],
): Promise<void> => {
  for (const spec of specs) await ensureIndex(collection, spec.keys, spec.options ?? {});
};
